import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { MessageCircle, ChevronRight } from "lucide-react"
import Header from "./header"
import CommunityChannelSidebar from "./community/CommunityChannelSidebar"
import CommunityFeedFilters from "./community/CommunityFeedFilters"
import CommunityPostComposer from "./community/CommunityPostComposer"
import CommunityPostCard from "./community/CommunityPostCard"
import {
  POSTS_PAGE_SIZE,
  createCommunityPost,
  fetchCommunityPosts,
  subscribeToNewCommunityPosts,
  getUserVerified,
} from "../community/community-service"
import { useAuth } from "../context/AuthContext"

export default function Community() {
  const { currentUser, userProfile } = useAuth()

  // Force the header into its "secondary pages" menu mode so it doesn't try to
  // toggle the main sportsbook view state (which this page doesn't control).
  const [currentView, setCurrentView] = useState("settings")
  const [verified, setVerified] = useState(false)

  const [channelsVisible, setChannelsVisible] = useState(true)

  const [filters, setFilters] = useState({
    channel: null, // null => All
    tag: null,
  })

  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [posting, setPosting] = useState(false)
  const [communityError, setCommunityError] = useState(null)

  // Pause realtime reads when the tab/window is hidden to reduce Firestore usage.
  const [isDocVisible, setIsDocVisible] = useState(true)

  const feedContainerRef = useRef(null)
  const bottomRef = useRef(null)
  const wasNearBottomRef = useRef(true)
  const suppressNextAutoScrollRef = useRef(false)

  const lastDocRef = useRef(null)
  const unsubNewPostsRef = useRef(null)
  const listenerKeyRef = useRef(null)
  const newestCreatedAtRef = useRef(null)

  useEffect(() => {
    const syncVisibility = () => {
      try {
        setIsDocVisible(document.visibilityState === "visible")
      } catch {
        setIsDocVisible(true)
      }
    }

    syncVisibility()
    document.addEventListener("visibilitychange", syncVisibility)
    return () => document.removeEventListener("visibilitychange", syncVisibility)
  }, [])

  useEffect(() => {
    if (!currentUser?.uid) return
    // Use the same "verified pill" concept TradeMetric uses.
    getUserVerified(currentUser.uid)
      .then(setVerified)
      .catch(() => setVerified(false))
  }, [currentUser?.uid])

  const newestCreatedAtMs = useMemo(() => {
    for (let i = posts.length - 1; i >= 0; i--) {
      if (posts[i]?.createdAt != null) return posts[i].createdAt
    }
    return null
  }, [posts])

  useEffect(() => {
    newestCreatedAtRef.current = newestCreatedAtMs
  }, [newestCreatedAtMs])

  // Initial load when filters change.
  useEffect(() => {
    if (!currentUser?.uid) return

    let cancelled = false
    const { channel, tag } = filters
    setCommunityError(null)
    setLoading(true)
    setHasMore(true)

    ;(async () => {
      try {
        const { posts: pagePosts, lastDoc } = await fetchCommunityPosts(
          { channel, tag },
          POSTS_PAGE_SIZE,
          null
        )
        if (cancelled) return

        setPosts([...pagePosts].reverse()) // oldest-first
        lastDocRef.current = lastDoc
        setHasMore(pagePosts.length === POSTS_PAGE_SIZE)
      } catch (err) {
        console.error("[Community] Load failed:", err)
        if (cancelled) return
        setCommunityError(err?.message ?? "Failed to load posts")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [currentUser?.uid, filters.channel, filters.tag])

  const startRealtimeListener = useCallback((key, channel, tag) => {
    if (unsubNewPostsRef.current) return
    if (newestCreatedAtRef.current == null) return

    listenerKeyRef.current = key
    unsubNewPostsRef.current = subscribeToNewCommunityPosts(
      { channel, tag },
      newestCreatedAtRef.current,
      (newPosts) => {
        setPosts((prev) => {
          const existing = new Set(prev.map((p) => p.id))
          const toAdd = newPosts.filter((p) => !existing.has(p.id))
          return toAdd.length ? [...prev, ...toAdd] : prev
        })
      }
    )
  }, [])

  // Realtime listener lifecycle for filter changes only.
  useEffect(() => {
    if (!currentUser?.uid) return
    const { channel, tag } = filters
    const nextKey = `${currentUser.uid}::${channel ?? "all"}::${tag ?? "all"}`

    if (unsubNewPostsRef.current) {
      unsubNewPostsRef.current()
      unsubNewPostsRef.current = null
    }
    listenerKeyRef.current = null
    if (!loading && isDocVisible) {
      startRealtimeListener(nextKey, channel, tag)
    }

    return () => {
      if (unsubNewPostsRef.current) {
        unsubNewPostsRef.current()
        unsubNewPostsRef.current = null
      }
      listenerKeyRef.current = null
    }
  }, [currentUser?.uid, filters.channel, filters.tag, loading, isDocVisible, startRealtimeListener])

  // If feed transitions from empty/new to having a newest post timestamp, start listener once.
  useEffect(() => {
    if (!currentUser?.uid) return
    if (loading) return
    if (!isDocVisible) return
    const { channel, tag } = filters
    const nextKey = `${currentUser.uid}::${channel ?? "all"}::${tag ?? "all"}`
    if (listenerKeyRef.current === nextKey && unsubNewPostsRef.current) return
    startRealtimeListener(nextKey, channel, tag)
  }, [currentUser?.uid, filters.channel, filters.tag, loading, isDocVisible, newestCreatedAtMs, startRealtimeListener])

  // When hidden, unsubscribe; when visible again, restart (if filters + newest are ready).
  useEffect(() => {
    if (!isDocVisible && unsubNewPostsRef.current) {
      unsubNewPostsRef.current()
      unsubNewPostsRef.current = null
      listenerKeyRef.current = null
      return
    }

    if (isDocVisible && currentUser?.uid && !loading) {
      const { channel, tag } = filters
      const nextKey = `${currentUser.uid}::${channel ?? "all"}::${tag ?? "all"}`
      if (!unsubNewPostsRef.current) {
        startRealtimeListener(nextKey, channel, tag)
      }
    }
  }, [isDocVisible, currentUser?.uid, loading, filters.channel, filters.tag, startRealtimeListener])

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return
    const lastDoc = lastDocRef.current
    if (!lastDoc) return

    // Loading older messages should not trigger auto-scroll to newest.
    suppressNextAutoScrollRef.current = true
    setLoadingMore(true)
    setCommunityError(null)

    fetchCommunityPosts(filters, POSTS_PAGE_SIZE, lastDoc)
      .then(({ posts: pagePosts, lastDoc: nextLastDoc }) => {
        setPosts((prev) => [...[...pagePosts].reverse(), ...prev]) // prepend older posts
        lastDocRef.current = nextLastDoc
        setHasMore(pagePosts.length === POSTS_PAGE_SIZE)
      })
      .catch((err) => {
        console.error("[Community] Load more failed:", err)
        setCommunityError(err?.message ?? "Failed to load more posts")
      })
      .finally(() => setLoadingMore(false))
  }, [filters, hasMore, loadingMore])

  // Auto-scroll to bottom (newest) when new posts appear and the user is near bottom.
  useEffect(() => {
    if (posts.length === 0) return
    const el = feedContainerRef.current
    if (!el) return
    if (suppressNextAutoScrollRef.current) {
      suppressNextAutoScrollRef.current = false
      wasNearBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 120
      return
    }

    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (wasNearBottomRef.current || isNearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
    }
    wasNearBottomRef.current = isNearBottom
  }, [posts.length, loading])

  const handleFeedScroll = useCallback(() => {
    const el = feedContainerRef.current
    if (!el) return
    wasNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
  }, [])

  const handleCreatePost = useCallback(
    async ({ channel, message, tags, images }) => {
      if (!currentUser?.uid) return
      setPosting(true)
      setCommunityError(null)

      const email = currentUser?.email
      const username =
        userProfile?.username ||
        userProfile?.full_name ||
        email?.split("@")[0] ||
        "Anonymous"

      try {
        const created = await createCommunityPost({
          uid: currentUser.uid,
          username,
          avatar: userProfile?.profile_picture || "",
          verified,
          channel,
          message,
          tags,
          images,
        })

        setPosts((prev) => {
          if (prev.some((p) => p.id === created.id)) return prev
          return [...prev, created]
        })
      } catch (err) {
        console.error("Failed to create community post:", err)
        setCommunityError(err?.message ?? "Failed to create post")
      } finally {
        setPosting(false)
      }
    },
    [currentUser?.uid, currentUser?.email, userProfile?.profile_picture, userProfile?.username, userProfile?.full_name, userProfile, verified]
  )

  const handleIncrementLike = useCallback((postId) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, likes: p.likes + 1 } : p)))
  }, [])

  const handleIncrementComment = useCallback((postId) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, replyCount: p.replyCount + 1 } : p)))
  }, [])

  const activeChannelLabel = useMemo(() => {
    if (!filters.channel) return "All channels"
    return filters.channel === "profitloss" ? "Profit & Loss" : "General"
  }, [filters.channel])

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header onToggle={setCurrentView} currentView={currentView} onShowNotifications={() => {}} />
      </div>

      <div className="h-[72px] shrink-0" />

      <div className="flex flex-grow min-h-0 overflow-visible pt-0 md:pt-4 pb-4">
        <div
          className={[
            "shrink-0 border-r border-gray-800 lg:w-56 flex flex-col sticky top-20 self-start",
            !channelsVisible ? "hidden lg:flex" : "",
          ].join(" ")}
        >
          <CommunityChannelSidebar
            activeChannel={filters.channel}
            onSelectChannel={(ch) => setFilters((prev) => ({ ...prev, channel: ch }))}
            onMobileHeaderClick={() => setChannelsVisible(false)}
          />
        </div>

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="shrink-0 sticky top-[65px] z-40 lg:static bg-gray-900 border-b border-gray-800">
            <div className="flex items-center gap-2 px-3 py-2">
              {!channelsVisible ? (
                <button
                  type="button"
                  onClick={() => setChannelsVisible(true)}
                  className="lg:hidden flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-gray-400 hover:text-white hover:bg-gray-800/40 -mx-1 px-1 py-0.5 rounded transition-colors"
                  aria-label="Show channels"
                >
                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                  Channels
                </button>
              ) : (
                <h2 className="text-sm font-semibold text-white/90 min-w-0 truncate">{activeChannelLabel}</h2>
              )}
            </div>
            <div className="px-2 pb-2">
              <CommunityFeedFilters
                tagFilter={filters.tag}
                onTagChange={(t) => setFilters((prev) => ({ ...prev, tag: t }))}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-2 py-2">
            {communityError ? (
              <div className="rounded border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                {communityError}
              </div>
            ) : null}

            {loading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-20 w-full rounded-md bg-gray-800/30 animate-pulse" />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center py-12 text-center px-4">
                <MessageCircle className="h-10 w-10 text-gray-500" />
                <p className="mt-2 text-sm font-medium text-white/90">No posts yet</p>
                <p className="text-xs text-gray-500">Be the first to post here</p>
              </div>
            ) : (
              <div
                ref={feedContainerRef}
                onScroll={handleFeedScroll}
                className="min-h-0 flex-1 overflow-y-auto space-y-0"
              >
                {hasMore ? (
                  <div className="flex justify-center py-2">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="rounded-full border border-gray-700 bg-gray-800/50 px-4 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-700/60 disabled:opacity-50"
                    >
                      {loadingMore ? "Loading..." : "Load more"}
                    </button>
                  </div>
                ) : posts.length > 0 ? (
                  <div className="flex justify-center py-2">
                    <span className="text-[11px] text-gray-500">No older messages</span>
                  </div>
                ) : null}

                {posts.map((post) => (
                  <CommunityPostCard
                    key={post.id}
                    post={post}
                    onTagClick={(tag) => setFilters((prev) => ({ ...prev, tag }))}
                    onCommentAdded={handleIncrementComment}
                    onLikeAdded={handleIncrementLike}
                  />
                ))}

                <div ref={bottomRef} className="h-2" />
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-800 bg-gray-900/30 px-2 py-2 sticky bottom-0 z-20 backdrop-blur-sm">
            <CommunityPostComposer
              channel={filters.channel ?? "general"}
              onChannelChange={(ch) => setFilters((prev) => ({ ...prev, channel: ch }))}
              onSubmit={handleCreatePost}
              posting={posting}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

